import React, {useEffect, useMemo, useState} from "react";
import { CanEdit, useIsReadOnlyManager } from "@/shared/readOnlyManager";
import {usePagination, useTable, useRowSelect, useSortBy} from "react-table";
import {
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Table, Container, Row, Col, Button, Label, Input,
} from "reactstrap";
import DatePicker from "react-datepicker";
import {getFirstDay, dateStrToYmd} from '@/shared/helpers';
import {
  flashError, flashSuccess, useDispatch,
} from "@/shared/components/flash";
import axios from "axios";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import {TABLE_COLUMNS} from "./PendingInviteColumns";
import {connect} from "react-redux";
import apiTableService from "@/services/apiTableService";
import IndeterminateCheckbox from "@/shared/components/form/IndeterminateCheckbox";
import {TableSkeleton} from "@/shared/components/Skeletons";
import InviteApprovalPopup from "@/containers/Manager/Home/components/InviteApprovalPopup";
import AwardHistoryPopup from "@/containers/Manager/Home/components/AwardHistoryPopup";

const ACTIONS = [{label: "Approve", name: "approved"}, {label: "Reject", name: "reject"},];
const queryPageSize = 10;

const PendingAwardApprovalsTable = ({auth, organization, program, assignedPermissions}) => {
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteApprovalParticipants, setInviteApprovalParticipants] = useState([]);
  const [statusName, setStatusName] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [isOpenHistory, setOpenHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  const [participant, setParticipant] = useState(null);
  const [events, setEvents] = useState(false);

  const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 2)));
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();

  const toggle = () => {
    setOpen((prevState) => !prevState);
  };

  const toggleHistory = async (row) => {
    if (!isOpenHistory) {
      if (row?.original?.userId){
        const response = await axios.get(`/organization/${organization.id}/program/${program.id}/participant/${row.original.userId}`)
        const events = response.data.events;
        if (events > 0) {
          setParticipant(response.data)
          setOpenHistory((prevState) => !prevState);
        }
      }
    } else {
      setParticipant(null)
      setOpenHistory((prevState) => !prevState);
    }
  };

  const readOnly = useIsReadOnlyManager();

  // Read Only Managers keep the invite-history link but not the approve/reject checkboxes.
  const SELECTION_COLUMN = {
    id: "selection",
    disableSortBy: true,
    Header: ({getToggleAllPageRowsSelectedProps}) => (readOnly ? null : <div>
      <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
    </div>), Cell: ({row}) => (
      row?.original?.events > 0 ?
        <div style={{cursor:'pointer'}} onClick={() => toggleHistory(row)} >-</div>
        : readOnly ? null : <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
      ),
  };

  let final_columns = React.useMemo(() => {
    return [SELECTION_COLUMN, ...TABLE_COLUMNS];
  }, [assignedPermissions, readOnly]);

  const onSelectAction = (name) => {
    const rows = selectedFlatRows.map((d) => d.original);
    if (rows.length === 0) {
      alert("Select participants");
      return;
    }
    setInviteApprovalParticipants(rows);
    setStatusName(name);
    toggle();
  };

  const tableInstance = useTable({
    columns: final_columns,
    data: useMemo(() => (participants ? participants?.results : []), [participants]),
    initialState: {
      pageIndex: 0, pageSize: queryPageSize, sortBy: [],
    },
    manualPagination: true,
    manualSortBy: true,
    pageCount: Math.ceil(participants?.count / queryPageSize),
    autoResetSortBy: false,
    autoResetExpanded: false,
    autoResetPage: false,
  }, useSortBy, usePagination, useRowSelect);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    state: {pageIndex, pageSize, sortBy},
  } = tableInstance;

  const fetchData = () => {
    let mounted = true;
    setLoading(true);
    if (organization?.id && program?.id) {
      const params = {
        start_date: dateStrToYmd(startDate),
        ...(endDate && { end_date: dateStrToYmd(endDate) }),
        ...(searchTerm && { search: searchTerm }),
      };

      apiTableService
        .fetchData({
          url: `/organization/${organization.id}/program/${program.id}/manager-pending-invites`,
          page: pageIndex,
          size: pageSize,
          filter: params,
          sortby: sortBy,
        })
        .then((items) => {
          if (mounted && items) {
            setParticipants(items);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
    return () => (mounted = false);
  };

  useEffect(() => {
    fetchData();
  }, [organization, program, pageIndex, pageSize, sortBy, startDate, endDate]);

  const ActionsDropdown = () => {
    return (<UncontrolledDropdown>
      <DropdownToggle caret className="dropdowntoggle">
        Actions
      </DropdownToggle>
      <DropdownMenu>
        {ACTIONS.map((item, index) => {
          return (<DropdownItem
            key={`action-dropdown-item-${index}`}
            onClick={() => onSelectAction(item.name)}
          >
            {item.label}
          </DropdownItem>);
        })}
      </DropdownMenu>
    </UncontrolledDropdown>);
  };

  const onSubmit = (values) => {

    try {
      if (inviteApprovalParticipants.length > 0) {
        let formData = {};
        setSaving(true);
        formData.record_id = inviteApprovalParticipants?.map((approvalParticipant) => approvalParticipant.id);
        formData.operation = statusName;
        formData.manager_id = auth.id;
        formData.rejection_note = statusName === "approved" ? `Approved by ${auth.name}` : values.rejection_note;

        axios
          .put(`/organization/${organization?.id}/program/${program?.id}/manager-pending-invites-approve-or-reject`, formData)
          .then((response) => {
            if (response.status === 200) {
              flashSuccess(dispatch, "Updated successfully!");
              setSaving(false);
              toggle();
              window.location.reload();
            }
          })
          .catch((error) => {
            flashError(dispatch, error.message);
            setSaving(false);
          });
      }
    } catch (error) {
      flashError(dispatch, error.message);
    }
  };

  const manualPageSize = [];
  return (<>
    <div className="users">
      <div className="p-3 mb-3" style={{ backgroundColor: 'white', borderRadius: '8px' }}>
        <Row>
          <Col md={3}>
            <Label style={{ display: 'block', marginBottom: '8px' }}>From Date</Label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control"
            />
          </Col>
          <Col md={3}>
            <Label style={{ display: 'block', marginBottom: '8px' }}>To Date</Label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              isClearable
              placeholderText="Select end date"
            />
          </Col>
          <Col md={4}>
            <Label style={{ display: 'block', marginBottom: '8px' }}>Search</Label>
            <Input
              type="text"
              placeholder="Search by name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </Col>
          <Col md={2}>
            <Label style={{ display: 'block', marginBottom: '8px' }}>&nbsp;</Label>
            <Button color="primary" onClick={fetchData} block>
              Search
            </Button>
          </Col>
        </Row>
      </div>
      <div className="header d-flex  justify-content-between">
        <div className="d-flex w-30 justify-content-between dropdown-group">
          <CanEdit>
            <ActionsDropdown/>
          </CanEdit>
        </div>
      </div>
      <div className="points-summary-table">
        {loading ? (
          <TableSkeleton variant="rect" rows={3} columns={7} width={"100%"} height={20} />
        ) : rows.length === 0 ? (
          <p className="text-center text-muted py-4">There are no pending award approvals.</p>
        ) : (
          <Table striped borderless size="md" className="table--stack-mobile" {...getTableProps()}>
            <thead>
            {headerGroups.map((headerGroup) => (<tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (<th {...column.getHeaderProps(column.getSortByToggleProps())}>
                {column.render("Header")}
                <span>
                  {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                </span>
              </th>))}
            </tr>))}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (<tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (<td
                    {...cell.getCellProps()}
                    data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                  >
                    {cell.render("Cell")}
                  </td>);
                })}
              </tr>);
            })}
            </tbody>
          </Table>
        )}
      </div>
    </div>

    <div>
      {!loading && rows.length > 0 && (<>
        <ReactTablePagination
          page={page}
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageOptions={pageOptions}
          pageSize={pageSize}
          pageIndex={pageIndex}
          pageCount={pageCount}
          setPageSize={setPageSize}
          manualPageSize={manualPageSize}
          dataLength={participants ? participants.count : 0}
        />
      </>)}
    </div>
    <InviteApprovalPopup
      isOpen={isOpen}
      setOpen={setOpen}
      toggle={toggle}
      auth={auth}
      organization={organization}
      program={program}
      statusName={statusName}
      inviteApprovalParticipants={inviteApprovalParticipants}
      rejection_notes={`User Rejected by ${auth?.name}`}
      onSubmit={onSubmit}
      loading={saving}
    />
    <AwardHistoryPopup
      isOpen={isOpenHistory}
      setOpen={setOpenHistory}
      toggle={toggleHistory}
      participant={participant}
    />
  </>);
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth, program: state.program, organization: state.organization, rootProgram: state.rootProgram,
  };
};
export { PendingAwardApprovalsTable as PendingInvitesTable };
export default connect(mapStateToProps)(PendingAwardApprovalsTable);
